// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { Dialog, FooterButton } from '@tls/treact-ui';
import { FlagRenderer } from '@tls/treact-ui/flags/all';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import useOpenProjectWithLock from 'hooks/useOpenProjectWithLock';
import { useProjectLockApi } from 'hooks/useProjectLockApi';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setImportedProjectId } from 'store/reducers/projectSettingsSlice';
import styled from 'styled-components';

// opening needs the lock, so the user has to release the one they hold first
export default function OpenImportedProjectDialog() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentUser } = useAuthContext();

  const { importedProjectId } = useAppSelector(s => s.projectSettings);
  const { projects } = useProjectsMetadata();

  const { unlock, loading: unlocking } = useProjectLockApi();
  const { openProjectWithLock, loading: opening } = useOpenProjectWithLock();

  const projectName = projects.find(p => p.projectId === importedProjectId)?.projectName ?? '';
  const lockedProject = projects.find(p => p.lockedBy && p.lockedBy === currentUser?.userName);
  const lockedProjectName = lockedProject?.projectName ?? '';

  const onClose = useCallback(() => dispatch(setImportedProjectId(undefined)), [dispatch]);

  const onConfirm = useCallback(async () => {
    if (!importedProjectId) return;

    if (lockedProject?.projectId && lockedProject.projectId !== importedProjectId) {
      const unlocked = await unlock(lockedProject.projectId);
      if (!unlocked) {
        ToastByStatus(500, t('project.unlock.title', { projectName: lockedProjectName }), t('project.unlock.error'));
        return;
      }
    }

    await openProjectWithLock(importedProjectId, 'editProject');
    onClose();
  }, [importedProjectId, lockedProject?.projectId, lockedProjectName, onClose, openProjectWithLock, t, unlock]);

  const loading = unlocking || opening;

  return (
    <Dialog
      enableClosing
      open={Boolean(importedProjectId)}
      size='small'
      onClose={onClose}
      data-testid='projects-open-imported-dialog'
    >
      <Dialog.DialogHeader label={t('projects.import.open.title')} showCloseButton />
      <Dialog.DialogContent>
        <div data-testid='projects-open-imported-dialog-content'>
          <Trans i18nKey='projects.import.open.question' values={{ projectName }} />
          {lockedProject && lockedProject.projectId !== importedProjectId && (
            <>
              <p>
                <Trans i18nKey='projects.import.open.closesCurrent' values={{ lockedProjectName }} />
              </p>
              <SameLine>
                <FlagRenderer flag='warningMessage' width='1.5rem' />
                <Trans i18nKey='project.lock.stopsTraining' />
              </SameLine>
            </>
          )}
        </div>
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            variant='secondary'
            label={t('cancel')}
            onClick={onClose}
            disabled={loading}
            data-testid='projects-open-imported-dialog-cancel-button'
          />,
          <FooterButton
            key='open'
            label={t('projects.import.open.confirm')}
            onClick={onConfirm}
            disabled={loading}
            iconKey={loading ? 'IconLoading' : undefined}
            data-testid='projects-open-imported-dialog-confirm-button'
          />,
        ]}
      />
    </Dialog>
  );
}

const SameLine = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  column-gap: 0.5rem;
`;
